import i18next from "i18next";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router";
import BasicInput from "../../components/BasicInput";
import Button from "../../components/Button";
import ConfirmDialog from "../../components/ConfirmDialog";
import Label from "../../components/Label";
import SimpleDialog from "../../components/SimpleDialog";
import * as formationsServices from "../../services/formationsServices";
import { routes, utils } from "../../utils";
import { Formation as FormationType } from "../../utils/types";
import ErrorMessage from "../../components/ErrorMessage";

export default function Formation() {
	const [formation, setFormation] = React.useState<FormationType>({ id: 0, name: "" });

	const [errors, setErrors] = React.useState<any>({});
	const [editing, setEditing] = React.useState<boolean>(false);
	const [deleteModal, setDeleteModal] = React.useState<boolean>(false);

	const { id } = useParams();

	useEffect(() => {
		if (id) {
			formationsServices
				.getFormation(id)
				.then((formation) => {
					setFormation(formation);
				})
				.catch((error) => {});
		}
	}, [id]);

	const navigate = useNavigate();

	const goBack = () => navigate(routes.DASHBOARD);

	const validate = () => {
		const errors: any = {};

		if (!formation.name) {
			errors.name = i18next.t("name-required");
		}

		setErrors(errors);

		return Object.keys(errors).length === 0 ? {} : errors;
	};

	const handelCancel = () => {
		if (editing) {
			setEditing(false);
		} else {
			navigate(-1);
		}
	};

	const handleCreateEditFormation = async () => {
		try {
			const errors = validate();
			if (Object.keys(errors).length !== 0) {
				return;
			}

			if (formation.id) {
				await formationsServices.updateFormation(formation);
			} else {
				await formationsServices.createFormation(formation);
			}

			toast.success(i18next.t(editing ? "formation-edit-success" : "formation-create-success"));
			goBack();
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
	};

	const handleDelete = async () => {
		try {
			await formationsServices.deleteFormation(formation.id);
			toast.success(i18next.t("formation-delete-success"));
			navigate(routes.FORMATIONS_LIST);
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
			setDeleteModal(false);
		}
	};

	const showValue = !!id && !editing;

	return (
		<SimpleDialog open={true} onClose={goBack}>
			<ConfirmDialog
				title={i18next.t("delete")}
				handleCancel={() => setDeleteModal(false)}
				handleConfirm={handleDelete}
				subtitle={i18next.t("formation-delete-confirm")}
				open={deleteModal}
			/>

			<div className="d-flex justify-content-between">
				<Label
					isTitle
					text={
						id
							? editing
								? i18next.t("entity-edit", { entity: i18next.t("Formation") })
								: i18next.t("formation-details")
							: i18next.t("entity-create", { entity: i18next.t("Formation") })
					}
				/>
				{editing && (
					<div
						className="text-danger"
						data-testid="delete-button"
						style={{ cursor: "pointer" }}
						onClick={() => setDeleteModal(true)}
					>
						{i18next.t("delete")}
					</div>
				)}
			</div>
			<BasicInput
				label={i18next.t("name")}
				value={formation.name}
				required={true}
				onChange={(e) => setFormation({ ...formation, name: e.target.value })}
				className="mt-2"
				error={errors.full_name}
				datatestid="name-input"
				showValue={showValue}
			/>
			{errors.name && <ErrorMessage error={errors.name} />}

			<div className="d-flex mt-4 justify-content-end">
				<Button
					label={i18next.t(id ? "go-back" : "cancel")}
					onClick={handelCancel}
					className="ms-4"
					secondary
				/>
				{id ? (
					editing ? (
						<Button
							label={i18next.t("save")}
							datatestid="save-button"
							onClick={handleCreateEditFormation}
							className="ms-4"
						/>
					) : (
						<Button
							label={i18next.t("entity-edit", { entity: i18next.t("Formation") })}
							datatestid="edit-button"
							onClick={() => setEditing(true)}
							className="ms-4"
							visible={utils.isAdminOrCollaborator()}
						/>
					)
				) : (
					<Button
						label={i18next.t("entity-create", { entity: i18next.t("Formation") })}
						datatestid="create-button"
						onClick={handleCreateEditFormation}
						className="ms-4"
					/>
				)}
			</div>
		</SimpleDialog>
	);
}
